import type {
  IRemoteCommandParamsBase,
  ISessionId,
  IUploadFileData,
  TConstructor,
  TIndex,
  TJQueryfiable,
  TRemoteCommandParameters,
} from "./Header.ts";

import {
  atKeys,
  defaultIfEmpty,
  isEmpty,
  isInstance,
  isNullish,
  notEmpty,
  notNull,
  notNullish,
  parseBoolean,
  parseDefaultString,
  parseError,
  parseJsonObject,
  parseRecordAs,
  parseString,
  recordValues,
  timeout,
} from "@de-xima/xima-common-js-lang";
import $ from "jquery";

import { showNotification } from "./Notification.ts";
import {
  $byId,
  changeStyleClass,
  classList as commonClassList,
  readBlobAsDataUri,
  type TElementSpecifier,
  toElements,
  toHtmlElements,
  toJQuery,
} from "@de-xima/xima-common-js-dom";

import queryString from "query-string";

// If cookies are disabled, we need to make sure the session ID is added to all URLs.
let urlSessionId: ISessionId | undefined;

let clientTopSave: { index?: number; originalOffsetTop?: number } = {};

type TDelayedRemoteCommandEntry =
  | { type: "pending"; reject: (error: unknown) => void; resolve: (value: never) => void }
  | { type: "resolved"; value: unknown };

const DelayedRemoteCommandMap = new Map<string, TDelayedRemoteCommandEntry>();

/**
 * Extract the session ID from{}  the URL, if it is set. Only when cookies are disabled.
 * @returns The prefix (eg. `jsessionid`) and the id.
 */
function getSessionIDFromLocation(): ISessionId | undefined {
  const match = /;(jsessionid)=(.+)$/.exec(window.location.pathname);
  if (notNull(match)) {
    return {
      id: match[2] ?? "",
      prefix: match[1] ?? "",
    };
  }
  return undefined;
}

/**
 * Used by `attachHover`, so we do not have to add the listener multiple times.
 * @param event Mouseenter event that was triggered.
 */
function hoverOnMouseenter(event: JQuery.TriggeredEvent): void {
  $(event.currentTarget).addClass("ui-state-hover");
}

/**
 * Used by `attachHover`, so we do not have to add the listener multiple times.
 * @param event Mouseleave event that was triggered.
 */
function hoverOnMouseleave(event: JQuery.TriggeredEvent): void {
  $(event.currentTarget).removeClass("ui-state-hover");
}

async function handleRemoteCommandResponse<R extends PrimeFaces.ajax.PrimeFacesArgs>(
  pfArgs: PrimeFaces.ajax.PrimeFacesArgs,
): Promise<R> {
  if (pfArgs === undefined) {
    throw new Error("No response received from server, does the remote command exist?");
  }
  const error = pfArgs.remoteCommandError;
  const success = pfArgs.remoteCommandSuccess;
  const delayId = pfArgs.remoteCommandDelayId;
  if (error !== undefined || success === false) {
    throw new Error(String(error ?? "Unhandled error during AJAX call, are you logged in?"));
  }
  if (typeof delayId === "string") {
    return new Promise((resolve, reject) => {
      // The asynchronous delayed response may have been received already
      const resolved = DelayedRemoteCommandMap.get(delayId);
      DelayedRemoteCommandMap.delete(delayId);
      if (resolved !== undefined && resolved.type === "resolved") {
        try {
          resolve(handleRemoteCommandResponse(resolved.value as R));
        } catch (e) {
          reject(e);
        }
      } else if (resolved === undefined) {
        DelayedRemoteCommandMap.set(parseString(delayId), { reject, resolve: resolve, type: "pending" });
      } else {
        reject(new Error(`Invalid state: ${resolved.type}`));
      }
    });
  }

  return pfArgs as R;
}

/**
 * Takes a class and overrides a method directly on the prototype, i.e. it also affects all current instances.
 * @template K Name of the method to override.
 * @param classObject Class whose method to override.
 * @param methodName Name of the method to override.
 * @param newMethod New function for the method. The first parameter to this function is the super method that was
 * overridden (with the this context already bound), followed by the parameters of the original method.
 */
export function overridePrototypeMethod<T, K extends keyof T>(
  classObject: TConstructor<T>,
  methodName: K,
  newMethod: T[K] extends (...args: infer Args) => infer R
    ? (this: T, superMethod: (...args: Args) => R, ...args: Args) => R
    : never,
): void {
  // Unsafe casts
  const protoOverrides = classObject.prototype as { $overrides$: Partial<Record<keyof T, unknown[]>> };
  const protoMethods = classObject.prototype as Record<keyof T, (this: T, ...args: unknown[]) => unknown>;

  const oldMethod = protoMethods[methodName];

  // Check if already overridden.
  protoOverrides.$overrides$ = protoOverrides.$overrides$ ?? {};
  protoOverrides.$overrides$[methodName] = protoOverrides.$overrides$[methodName] ?? [];
  const newMethodIndex = protoOverrides.$overrides$[methodName]?.indexOf(newMethod);
  if (newMethodIndex !== undefined && newMethodIndex >= 0) {
    return;
  }

  // If not, override
  protoOverrides.$overrides$[methodName]?.push(newMethod);
  protoMethods[methodName] = function (this: T, ...args: unknown[]) {
    return newMethod.call(this, oldMethod.bind(this), ...args);
  };
}

/**
 * Takes a class and overrides a method directly on the prototype, i.e. it also
 * affects all current instances.
 * @typeparam M Method to override.
 * @typeparam T Type of the class with the method.
 * @param classObject Class whose method to override.
 * @param method Method to override.
 * @param newMethod New function for the method. The first parameter to this
 * function is the super method that was overridden (with the this context
 * already bound), followed by the parameters of the original method.
 */
export function overridePrivatePrototypeMethod<M extends (...args: never[]) => unknown, T extends ThisType<M>>(
  classObject: TConstructor<T>,
  methodName: string,
  _method: M,
  newMethod: (this: T, superMethod: (...args: Parameters<M>) => ReturnType<M>, ...args: Parameters<M>) => ReturnType<M>,
): void {
  overridePrototypeMethod<T, keyof T>(
    classObject,
    methodName as keyof T,
    newMethod as unknown as T[keyof T] extends (...args: infer Args) => infer R
      ? (this: T, superMethod: (...args: Args) => R, ...args: Args) => R
      : never,
  );
}

/**
 * Creates a new empty array for the params of the given remote command. When
 * the remote command is `BackendCommand.Designer.myCommand`, use
 *
 * ```typescript
 * remoteCommandParams<["Designer", "myCommand"]>();
 * ```
 *
 * @typeparam K Path of the remote command, e.g. `["Designer", "myCommand"]`.
 * @returns A new empty array with the correct type for the given remote command.
 */
export function remoteCommandParams<K extends string[]>(): TRemoteCommandParameters<
  TIndex<Formcycle.IBackendCommand, K>
> {
  return [] as TRemoteCommandParameters<TIndex<Formcycle.IBackendCommand, K>>;
}

/**
 * Converts a jquery deferred or pseudo-like promise object to a proper Promise
 * implementation. The jquery deferred lacks methods such as `finally`.
 * @param deferred A JQuery deferred or base promise object.
 * @returns A proper Promise implementation for the deferred.
 */
export function promiseToDeferred<T>(promise: Promise<T>): JQuery.Deferred<T, unknown, unknown> {
  const deferred = $.Deferred<T>();
  promise.then(
    (value) => deferred.resolve(value),
    (error) => deferred.reject(error),
  );
  return deferred;
}

/**
 * Converts a jquery deferred or pseudo-like promise object to a proper Promise
 * implementation. The jquery deferred lacks methods such as `finally`.
 * @param deferred A JQuery deferred or base promise object.
 * @returns A proper Promise implementation for the deferred.
 */
export async function deferredToPromise<T>(
  deferred: JQuery.PromiseBase<
    T,
    unknown,
    unknown,
    unknown,
    unknown,
    unknown,
    unknown,
    unknown,
    unknown,
    unknown,
    unknown,
    unknown
  >,
): Promise<T> {
  const result = await deferred;
  return result;
}

/**
 * @param data An object with the data to send.
 * @returns The data, converted into an array suitable for a PrimeFaces
 * remote command.
 */
export function toServerCallbackParameter<T extends Record<string, unknown>>(
  data: T,
): PrimeFaces.ajax.RemoteCommandParams<T> {
  const params = [] as PrimeFaces.ajax.RemoteCommandParams;
  for (const key of Object.keys(data)) {
    const value = data[key];
    if (Array.isArray(value)) {
      for (const v of value as unknown[]) {
        params.push({ name: key, value: v });
      }
    } else {
      params.push({ name: key, value: value });
    }
  }
  return params as PrimeFaces.ajax.RemoteCommandParams<T>;
}

/**
 * Called by the server when the response to a delayed (async) remote command
 * is ready.
 *
 * The server may delay the response to a remote command when it cannot compute
 * the response synchronously. Once the response is available the server calls
 * this method.
 * @param delayId ID of the delayed remote command to resolve.
 */
export async function resolveDelayedRemoteCommand(delayId: string, params: string): Promise<void> {
  const pending = DelayedRemoteCommandMap.get(delayId);
  const parsedParams = parseJsonObject(params);
  // This function may be called before or after we received the response that
  // the command should be delayed
  if (pending !== undefined && pending.type === "pending") {
    DelayedRemoteCommandMap.delete(delayId);
    try {
      const result = await handleRemoteCommandResponse(parsedParams);
      pending.resolve(result as never);
    } catch (e) {
      pending.reject(e);
    }
  } else if (pending === undefined) {
    DelayedRemoteCommandMap.set(delayId, { type: "resolved", value: parsedParams });
  } else {
    console.error(`Cannot resolve delayed remote command for ID ${delayId}`, pending);
  }
}

/**
 * @param remoteCommand Name of the remote command or the remote command
 * function itself.
 * @param data Data passed to the remote command. When an array of name-value
 * pairs, they are passed directly to the remote command. When a key-value
 * object is given, each key-value pair is converted to a name-value pair.
 * @returns Data returned by the remote command.
 * @throws When the remote command was not successful.
 */
export async function runRemoteCommand<
  T extends Record<string, unknown>,
  R extends PrimeFaces.ajax.PrimeFacesArgs & IRemoteCommandParamsBase,
>(
  remoteCommand: PrimeFaces.ajax.RemoteCommand<T, R> | undefined,
  data: PrimeFaces.ajax.RemoteCommandParams<T> = [],
): Promise<Partial<R>> {
  let response: PrimeFaces.ajax.ResponseData<R> | undefined;
  try {
    response = remoteCommand !== undefined ? await remoteCommand(data) : undefined;
  } catch (e) {
    if (typeof e === "object" && notNull(e) && "textStatus" in e && "errorThrown" in e) {
      const errorObj = parseRecordAs(e, { errorThrown: parseString, textStatus: parseString });
      throw new Error(
        `Network request failed: ${errorObj.textStatus}: ${defaultIfEmpty(errorObj.errorThrown, "unknown")}`,
      );
    }

    throw parseError(e);
  }
  const pfArgs = response?.jqXHR?.pfArgs ?? {};
  return handleRemoteCommandResponse(pfArgs);
}

/**
 * Similar to `PrimeFaces.invokeDeferredRenders`, but invokes all renders that
 * are children of the given container, or all renders when no container is
 * given.
 * @param container Optional container. If given, only renders inside that
 * container are invoked.
 */
export function invokeAllDeferredRenders(container?: TElementSpecifier<HTMLElement>): void {
  const containerElement = toElements(container)[0];
  const ids = (PrimeFaces.deferredRenders ?? [])
    .filter((render) => {
      if (containerElement !== undefined && notNullish(render.container)) {
        const renderContainer = document.getElementById(render.container);
        return containerElement === renderContainer || containerElement.contains(renderContainer);
      }

      return true;
    })
    .map((render) => render.container);
  const uniqueIds = [...new Set(ids)];
  for (const id of uniqueIds) {
    PrimeFaces.invokeDeferredRenders(id);
  }
}

/**
 * @param element Element for which to find the tooltip. Must be the element to which
 * the tooltip was assigned (via <p:tooltip for="...">)
 * @returns The PrimeFaces tooltip widget referring to the given element; or `undefined` if no such tooltip exists.
 */
export function findTooltipWidgetFor(element: TElementSpecifier<HTMLElement>): PrimeFaces.widget.Tooltip | undefined {
  const el = toElements(element)[0];
  if (el !== undefined) {
    for (const tooltip of PrimeFaces.getWidgetsByType(PrimeFaces.widget.Tooltip)) {
      const target = toElements(tooltip.target)[0];
      if (target !== undefined && (el === target || target.contains(el))) {
        return tooltip;
      }
    }
  }
  return undefined;
}

/**
 * Tries to find the widget for the given element. The following attempts are made:
 * - Search the list of all known widgets for a widget whose DOM element contains the
 * given element.
 * - Search for a widget with the same ID as the ID of the UI widget. The UI widget
 * is located by searching for the nearest parent with the class <code>ui-widget</code>
 * - Check whether containing UI widget has a data-widget-var attribute and search
 * for a widget with that widget variable.
 * @template T Type of the widget.
 * @param el Element for which to find the widget instance.
 * @param widgetClass Expected type of the widget. No check is performed if not given.
 * @returns The widget for the element, if found.
 */
export function findWidgetForElement<T extends TConstructor<unknown>>(
  el: TElementSpecifier<HTMLElement>,
  widgetClass: T,
): InstanceType<T> | undefined {
  const element = toHtmlElements(el)[0];
  if (element === undefined) {
    return undefined;
  }

  // Try the DOM element
  for (const widget of recordValues(PrimeFaces.widgets)) {
    if (widget instanceof widgetClass) {
      const jq = widget.getJQ().get(0);
      if (jq !== undefined && (jq === element || jq.contains(element))) {
        return widget as InstanceType<T>;
      }
    }
  }

  // Try to find the widget variable
  const selector = ".ui-widget,.ui-layout-container,.ui-inplace,.ui-calendar";
  const container = $(element).is(selector) ? $(element) : $(element).closest(selector);
  const widgetVar = parseString(container.data("widgetVar"));
  if (notEmpty(widgetVar)) {
    return getWidgetByVar(widgetVar, widgetClass);
  }

  // Try the ID
  return findWidgetForId(container.attr("id") ?? "", widgetClass);
}

/**
 * Attempts to locate the widget for an element, given the ID (not widgetVar) of the main DOM element of the widget (not the widget.jq.id)
 * @param targetId ID of the DOM element of the widget.
 * @param widgetClass Expected type of the widget. No check is performed if not given.
 * @returns The widget for the element, if found.
 */
export function findWidgetForId<T extends TConstructor>(targetId: string, widgetClass: T): InstanceType<T> | undefined {
  if (isEmpty(targetId) || widgetClass === undefined) {
    return undefined;
  }
  for (const widgetVar of Object.keys(PrimeFaces.widgets)) {
    if (!(widgetVar in PrimeFaces.widgets)) {
      continue;
    }
    const widget = PrimeFaces.widgets[widgetVar];
    if (isNullish(widget)) {
      // Widget existed previously, but was removed via AJAX
      continue;
    }
    const jq = widget.jq;
    const id = jq?.attr("id") ?? "";
    if (id === targetId) {
      return getWidgetByVar(widgetVar, widgetClass);
    }
  }
  return undefined;
}

/**
 * Prevents the default of the given event when it is a keyboard event where the
 * Enter key was pressed on an input element. I.e. prevents form submission when
 * pressing Enter in an input field.
 * @param event Event to check.
 */
export function preventSubmissionOnEnter(event: Event): void {
  if (event instanceof KeyboardEvent && event.key === "Enter") {
    const target = event.target;
    // Note: Must allow Enter in e.g. <textarea>
    if (target instanceof HTMLInputElement) {
      event.preventDefault();
    }
  }
}

/**
 * @template T Type of the widget.
 * @param widgetVar Name of the widget variable of the widget to find.
 * @param widgetClass Expected type of the widget. No check is performed if not given.
 * @returns The widget with the given widget variable.
 */
export function getWidgetByVar<T extends TConstructor>(widgetVar: string, widgetClass: T): InstanceType<T> | undefined {
  const widget = PrimeFaces.widgets[widgetVar];
  if (isNullish(widget) || widgetClass === undefined) {
    return undefined;
  }
  if (!isInstance(widget, widgetClass)) {
    console.error(`Widget for var '${widgetVar}' not of the correct class ${String(widgetClass)}`);
    return undefined;
  }
  return widget as unknown as InstanceType<T> | undefined;
}

/**
 * Searches for all dialogs in the current page and closes them.
 * @param filter Optional filter. If given, only dialogs matching this filter
 * are closed.
 */
export function closeAllDialogs(filter?: ((dialog: PrimeFaces.widget.Dialog) => boolean) | undefined): void {
  for (const dialog of PrimeFaces.getWidgetsByType(PrimeFaces.widget.Dialog)) {
    if (isNullish(filter) || filter(dialog)) {
      dialog.hide();
    }
  }
}

/**
 * @deprecated Refactor into xima-common-js
 * @param element Element to check.
 * @param treatPartialAsInvisible If true, elements that are only partially visible return `false`.
 * @returns Whether the given element is visible in the browser window.
 */
export function isScrolledIntoView(element: TElementSpecifier<HTMLElement>, treatPartialAsInvisible: boolean): boolean {
  const $el = toJQuery(element);
  if ($el.length === 0) {
    return false;
  }
  const rect = $el.offset();
  const elemTop = rect?.top ?? 0;
  const elemBottom = (rect?.top ?? 0) + ($el.height() ?? 0);

  // Only completely visible elements return true:
  if (treatPartialAsInvisible) {
    return elemTop >= 0 && elemBottom <= window.innerHeight;
  }

  return elemTop < window.innerHeight && elemBottom >= 0;
}

/**
 * Scrolls the element into the visible area of the browser, if it is not there already.
 * @param element Element to which to scroll.
 * @param treatPartialAsInvisible If true, elements that are only partially visible return `false`.
 * @param forceNative Whether to force the use of the native method for scrolling into view.
 */
export function scrollIntoViewIfNeeded(
  element: TElementSpecifier<HTMLElement>,
  treatPartialAsInvisible?: boolean,
  forceNative?: boolean,
): void {
  if (!isScrolledIntoView(element, treatPartialAsInvisible ?? false)) {
    scrollIntoView(element, forceNative);
  }
}

/**
 * Scrolls the element into the visible area of the browser
 * @param element Element to be scrolled into view.
 * @param  forceNative If `false`, prefers to scroll via the `$.fn.nanoScroller`
 * method. If `true`, always uses the native `Element#scrollIntoView` method.
 */
export function scrollIntoView(element: TElementSpecifier<HTMLElement>, forceNative?: boolean): void {
  const $element = toJQuery(element);
  if ($element.length < 1) {
    return;
  }
  const nano = $element.closest(".nano");
  if (forceNative === true || nano.length === 0) {
    const el = $element.get(0);
    if (el !== undefined && el instanceof HTMLElement && typeof el.scrollIntoView === "function") {
      el.scrollIntoView({ behavior: "smooth" });
    }
  } else {
    const elemOffset = $element.offset();
    const nanoOffset = nano.offset();
    nano.nanoScroller({
      scrollTop: Math.max(0, (elemOffset?.top ?? 0) - (nanoOffset?.top ?? 0)),
    });
  }
}

/**
 * If cookies are disabled and the `jessionid` is added to the URL, that session id.
 * @returns The current URL session ID.
 */
export function getURLSessionId(): ISessionId | undefined {
  if (urlSessionId === undefined) {
    urlSessionId = getSessionIDFromLocation();
  }
  return urlSessionId;
}

/**
 * Replaces the session ID in the URL with the given new session ID.
 * @param sid New session ID.
 */
export function updateURLSessionId(sid: ISessionId): void {
  urlSessionId = sid;
  // Update the browser URL
  if (typeof window.history === "object") {
    if (typeof window.history.replaceState === "function") {
      window.history.replaceState({}, document.title, encodeURL(window.location.href));
    } else if (typeof window.history.pushState === "function") {
      window.history.pushState({}, document.title, encodeURL(window.location.href));
    }
  }
  // Update action of all forms
  for (const form of $("form")) {
    const $form = $(form);
    $form.attr("action", encodeURL($form.attr("action") ?? ""));
  }
  // Update href of all anchors
  for (const anchor of $("a")) {
    const $anchor = $(anchor);
    const href = $anchor.attr("href");
    if (notEmpty(href)) {
      $anchor.attr("href", encodeURL(href));
    }
  }
}

/**
 * @param url URL to resolve
 * @returns The given URL, resolved relative to the current location.
 */
export function resolveURL(url: string): string {
  const a = document.createElement("a");
  a.href = url;
  return a.href;
}

/**
 * Takes an URL and encodes it, similar to `HttpServletResponse#encodeURL`. Adds the URL jsessionid,
 * if one was found to the URL and returns it. The given URL is resolved against the current page.
 * @param url URL to be encoded.
 * @param prefixContextPath If `true`, add the context path in front of the URL. Must be a relative URL,
 * eg. `/ui/forms`
 * @returns The encoded URL.
 */
export function encodeURL(url: string, prefixContextPath: boolean = false): string {
  const sid = getURLSessionId();
  if (isEmpty(url)) {
    return url;
  }

  // Need to resolve URL first, otherwise host property is not populated in IE.
  const urlBuilder = document.createElement("a");
  urlBuilder.href = resolveURL(url);

  // Do not encode external URLs
  // Unless the URL does contain a session ID
  const hasSessionId = sid !== undefined ? urlBuilder.pathname.indexOf(`;${sid.prefix}=`) >= 0 : false;
  if (
    !hasSessionId &&
    (urlBuilder.hostname !== window.location.hostname ||
      String(urlBuilder.port ?? "80") !== String(window.location.port ?? "80"))
  ) {
    return url;
  }

  // Add context path, if requested
  if (prefixContextPath) {
    // Add slash if missing
    const path = urlBuilder.pathname;
    urlBuilder.pathname = getContextPath() + (path.startsWith("/") ? "" : "/") + path;
  }

  if (sid !== undefined) {
    // Check if the URL got a jsessionid already, if so, remove it
    const pathname = urlBuilder.pathname ?? "";
    const match = new RegExp(`;${sid.prefix}=.+$`).exec(pathname);
    if (notNull(match)) {
      urlBuilder.pathname = urlBuilder.pathname.substring(0, match.index);
    }

    // Add the "jessionid"
    urlBuilder.pathname = `${urlBuilder.pathname};${sid.prefix}=${sid.id}`;
  }

  return urlBuilder.href;
}

/**
 * @param element Scrolled element with a position to save.
 */
export function saveClientTop(element: TElementSpecifier<HTMLElement>): void {
  const $element = toJQuery(element);
  const offset = $element.offset() ?? { top: 0 };
  clientTopSave = {
    index: $element.index(),
    originalOffsetTop: offset.top ?? 0,
  };
}

/**
 * Attempts to restore the client top position of the given element
 * to the same value as saved by `saveClientTop`; by setting the
 * scrolling of the given scroll container.
 * @param element Element for which to restore the scroll position.
 * @param scrollContainer Selector for the scoll container. Must be a parent of the element.
 * @param useIndex If true, uses the n-th child of the given element. n is the index as previously stored.
 */
export function restoreClientTop(
  element: TElementSpecifier<HTMLElement>,
  scrollContainer: string,
  useIndex: boolean,
): void {
  if (clientTopSave.originalOffsetTop === undefined) {
    console.warn("Cannot set scroll top, no scroll height saved.");
    return;
  }
  let $el = toJQuery(element);
  if ($el.length === 0) {
    console.warn("Cannot restore scrolling position, no element given.", element, scrollContainer);
    return;
  }
  if (useIndex) {
    const index = clientTopSave.index;
    const childCount = $el.children().length;
    if (childCount === 0 || index === undefined) {
      return;
    }
    const atIndex = $el.children().get(index < childCount ? index : childCount - 1);
    $el = atIndex !== undefined ? $(atIndex) : $();
  }
  const $scrollContainer = $el.closest(scrollContainer);
  if ($scrollContainer.length === 0) {
    console.warn("Cannot restore scrolling position, no scroll container found.", element, scrollContainer);
    return;
  }
  let scrollTop = $scrollContainer.scrollTop() ?? 0;
  const offset = $el.offset();
  scrollTop += (offset?.top ?? 0) - clientTopSave.originalOffsetTop;
  $scrollContainer.scrollTop(scrollTop > 0 ? scrollTop : 0);
  clientTopSave = {};
}

/**
 * @returns Whether the top scroll position was previously saved.
 */
export function hasClientTopSaved(): boolean {
  return clientTopSave.originalOffsetTop !== undefined;
}

/**
 * @returns Current context path of this application.
 */
export function getContextPath(): string {
  return parseString($("body").data("contextPath"));
}

/**
 * @returns Whether this application is running in dev mode.
 */
export function isDevMode(): boolean {
  return parseBoolean(window.document.body.dataset.devMode);
}

/**
 * @returns The current UI locale code, e.g. `en` or `de`.
 */
export function getLocaleCode(): string {
  return parseDefaultString(window.document.body.dataset.localeCode, "en");
}

/**
 * Copies the currently selected text to the clipboard. Logs an error
 * if the text cannot be copied.
 * @returns Whether the text was copied successfully.
 */
export function copyToClipboard(): boolean {
  try {
    if (typeof document.execCommand === "function") {
      const success = document.execCommand("copy");
      return success;
    }

    console.warn("Cannot copy to clipboard, document.execCommand is undefined");
    return false;
  } catch (e: unknown) {
    console.error("copy failed", e);
    return false;
  }
}

export function copyTextToClipboard(text: string): boolean {
  const isRTL = document.documentElement.getAttribute("dir") === "rtl";
  const fakeElem = document.createElement("textarea");
  fakeElem.style.fontSize = "12pt";
  fakeElem.style.border = "0";
  fakeElem.style.padding = "0";
  fakeElem.style.margin = "0";
  fakeElem.style.position = "absolute";
  fakeElem.style[isRTL ? "right" : "left"] = "-9999px";
  const yPosition = window.scrollY ?? document.documentElement.scrollTop;
  fakeElem.style.top = `${yPosition}px`;
  fakeElem.setAttribute("readonly", "");
  fakeElem.value = text;
  document.body.appendChild(fakeElem);
  fakeElem.select();
  fakeElem.setSelectionRange(0, fakeElem.value.length);
  const success = copyToClipboard();
  fakeElem.parentElement?.removeChild(fakeElem);
  return success;
}

/**
 * Copies text from the target element to the clipboard. Use `data-copy-text`
 * to specify the text to copy, `data-copy-success` to specify the text to
 * display on success, and `data-copy-error` to specify the text to display on
 * error.
 * @param target Target element.
 */
export function copyToClipboardFor(target: TElementSpecifier<HTMLElement>): void {
  const $target = toJQuery(target);
  const text = parseString($target.data("copyText"));
  if (text.length > 0) {
    const success = copyTextToClipboard(text);
    const message = parseString($target.data(success ? "copySuccess" : "copyError"));
    const severity = success ? "info" : "error";
    if (notEmpty(message)) {
      showNotification(
        {
          detail: message,
          severity: severity,
          summary: message,
        },
        undefined,
        false,
      );
    }
  }
}

/**
 * @param element A DOM element.
 * @returns The code mirror instance for the element, if it exists.
 */
export function getCodeMirror(element: TElementSpecifier<HTMLElement>): CodeMirror.Editor | undefined {
  const nativeEl = toElements(element)[0];
  const cm = atKeys(nativeEl, ["CodeMirror"]);
  return notNull(cm) && typeof cm === "object" ? (cm as CodeMirror.Editor) : undefined;
}

/**
 * Returns the current PrimeFaces versions. Checks if the version is an invalid
 * string. If so, returns some sensible default.
 * @returns The current PrimeFaces version.
 */
export function getPrimeFacesVersion(): string {
  if (typeof PrimeFaces === "object" && typeof PrimeFaces.VERSION === "string") {
    // Sometimes it is set to "${project.version}"
    if (!PrimeFaces.VERSION.startsWith("$")) {
      return PrimeFaces.VERSION;
    }
  }
  console.warn("PrimeFaces.VERSION is not set, returning random version");
  return `${Math.floor(Math.random() * 100)}.${Math.floor(Math.random() * 100)}`;
}

/**
 * @returns The current formcycle version of the application, or the current
 * timestamp when the version could not be determined.In development mode,
 * always returns the current timestamp.
 * @deprecated for security reasons, the formcycle version is no longer included in the body.
 * For example, getServerStartTime() can be used for caching.
 */
export function getFcVersion(): string {
  if (isDevMode()) {
    return String(Date.now());
  }

  return parseDefaultString($("body").data("fcVersion"), String(Date.now()));
}

/**
 * @returns The current start time of the server, or the current
 * timestamp when the start time could not be determined.In development mode,
 * always returns the current timestamp.
 */
export function getServerStartTime(): string {
  if (isDevMode()) {
    return String(Date.now());
  }

  return parseDefaultString($("body").data("serverStartTime"), String(Date.now()));
}

/**
 * PrimeFaces adds the style class `ui-state-hover` to elements when they are
 * hovered. Sometimes we need to get the same behavior for custom elements. This
 * method takes a set of elements and adds event listeners so that the class
 * `ui-state-hover` is added when the user moves the mouse over the element, and
 * removed when the mouse leave the element.
 * @param elements Elements to which to attach the hover behavior.
 */
export function attachHover(elements: TElementSpecifier<HTMLElement>): void {
  const $elements = toJQuery(elements);
  $elements.off("mouseenter", hoverOnMouseenter).on("mouseenter", hoverOnMouseenter);
  $elements.off("mouseleave", hoverOnMouseleave).on("mouseleave", hoverOnMouseleave);
}

/**
 * Asks the user to select a file, and reads the file data. Must be handled by
 * synchronously by the page where the click ocurred or the browser will/may
 * block the file chooser popup.
 * @returns A promise that resolves with the data of the persist file selected by
 * the user.
 */
export async function readUploadFile(accept: string): Promise<IUploadFileData | undefined> {
  const promise = new Promise<IUploadFileData | undefined>((resolve, reject) => {
    const input = $("<input>", {
      accept: accept,
      style: "display:none;",
      type: "file",
    });
    $("body").append(input);
    input.on("change", (inputEvent) => {
      const target = inputEvent.target;
      if (target instanceof HTMLInputElement) {
        const files = target.files;
        const file = files?.[0];
        if (isNullish(files) || files.length !== 1 || file === undefined) {
          input.remove();
          resolve(undefined);
        } else {
          const fileName = file.name;
          const fr = new FileReader();
          fr.onerror = () => {
            input.remove();
            reject(fr.error);
          };
          fr.onload = (fileEvent) => {
            const data = fileEvent.target?.result;
            input.remove();
            if (typeof data === "string") {
              resolve({ data, fileName });
            } else {
              resolve(undefined);
            }
          };
          fr.readAsText(file);
        }
      }
    });
    input.trigger("click");
  });
  return promise;
}

/**
 * Checks whether two URLs have the same origin. This checks whether the
 * protocol, host and port are identical.
 * @deprecated Use module `@de-xima/xima-common-js-dom`. This will be removed in version 8.
 * @param url1 URL to check.
 * @param url2 URL with the expected origin.
 * @returns `true` both URLs have the same origin, `false` otherwise.
 */
export function isSameOrigin(url1: string | URL, url2: string | URL): boolean {
  const u1 = typeof url1 === "string" ? new URL(url1) : url1;
  const u2 = typeof url2 === "string" ? new URL(url2) : url2;
  return u1.protocol === u2.protocol && u1.hostname === u2.hostname && u1.port === u2.port;
}

export function inputMaxLengthCallback(input: HTMLInputElement, callback: (value: string) => void): void {
  const value = input.value;
  const maxLength = input.maxLength;
  const oldValue = input.dataset.oldValue;
  if (value.length === maxLength && value !== oldValue) {
    input.dataset.oldValue = value;
    callback(value);
  }
}

// @deprecated Use the export directly...
/** @deprecated Use `import { parseUrl } from "query-string";` */
export const parseUrl: (url: string, options?: queryString.ParseOptions) => queryString.ParsedUrl =
  queryString.parseUrl;
/** @deprecated Use `import { stringifyUrl } from "query-string";` */
export const stringifyUrl: (object: queryString.UrlObject, options?: queryString.StringifyOptions) => string =
  queryString.stringifyUrl;

/**
 * Converts a JQuery XHR to a promise that resolve when the XHR finishes.
 * @deprecated Use module `@de-xima/xima-common-js-dom`. This will be removed in version 8.
 * @param xhr XHR to convert.
 * @returns Result of the XHR request.
 */
export async function jqXhr2Promise(
  xhr: JQueryXHR,
): Promise<{ data: unknown; status: JQuery.Ajax.SuccessTextStatus; xhr: JQuery.jqXHR }> {
  return new Promise((resolve, reject) => {
    xhr
      .then((data, status, xhr) => {
        resolve({ data: data as unknown, status, xhr });
      })
      .catch((e) => reject(e));
  });
}

/**
 * @deprecated Use module `@de-xima/xima-common-js-dom`. This will be removed in version 8.
 * @param element Element to wrap in JQuery.
 * @returns The element wrapped in a JQuery instance.
 */
export function toJquery(element: TJQueryfiable): JQuery<HTMLElement> {
  if (isNullish(element)) {
    return $<HTMLElement>();
  }
  if (typeof element === "string") {
    return $(element);
  }
  if (element instanceof HTMLElement) {
    return $(element);
  }
  if (element instanceof Document) {
    // Not strictly type-safe, but better than having to deal with different
    // type arguments all the time for now. Change return type to
    // JQuery<HTMLElement> | JQuery<Document> when/if this becomes an issue.
    return $(document) as unknown as JQuery;
  }
  return element;
}

/**
 * @deprecated Use module `@de-xima/xima-common-js-dom`. This will be removed in version 8.
 * @param element Element to wrap in JQuery.
 * @returns The element wrapped in a JQuery instance.
 */
export function toJqueryHtml(element: TJQueryfiable): JQuery<HTMLElement> {
  if (isNullish(element)) {
    return $<HTMLElement>();
  }
  if (typeof element === "string") {
    return $(element);
  }
  if (element instanceof HTMLElement) {
    return $(element);
  }
  if (element instanceof Document) {
    return $();
  }
  return element;
}

/**
 * @deprecated Use module `@de-xima/xima-common-js-dom`. This will be removed in version 8.
 * @param id ID of the element to find.
 * @returns Element with the given ID, empty when no element with the given ID exists.
 */
export function getElementById(id: string | undefined): JQuery<HTMLElement> {
  return id ? $byId(id) : $();
}

/**
 * @deprecated Use module `@de-xima/xima-common-js-dom`. This will be removed in version 8.
 * @param element Element to unwrap.
 * @returns The unwrapped HTML element.
 */
export function toElement(element: TJQueryfiable): Document | HTMLElement | undefined {
  const $element = toJquery(element);
  return $element.get(0);
}

/**
 * @deprecated Use module `@de-xima/xima-common-js-dom`. This will be removed in version 8.
 * @param element DOM element to process.
 * @returns Array of classes of the DOM element.
 */
export function classList(element: TJQueryfiable): string[] {
  return element instanceof Document ? [] : commonClassList(element);
}

/**
 * Adds or removes the style class on the given element.
 * @deprecated Use module `@de-xima/xima-common-js-dom`. This will be removed in version 8.
 * @param element Element to modify.
 * @param styleClass Style class to be added or removed.
 * @param state Optional state. When `true`, adds the style class. When `false`,
 * removes the style class. When `undefined`, adds the style class if it is not set and vice versa.
 */
export function toggleStyleClass(element: TJQueryfiable, styleClass: string, state?: boolean): void {
  if (!(element instanceof Document)) {
    changeStyleClass(element, styleClass, state);
  }
}

/**
 * @deprecated Use module `primefaces` directly: `PrimeFaces.getWidgetsByType`.
 * @param type Expected type of the widget.
 * @returns All currently existing widget instances of the given type.
 */
export function findWidgetsOfType<T extends TConstructor<unknown>>(type: T): InstanceType<T>[] {
  if (isNullish(type)) {
    return [];
  }
  return PrimeFaces.getWidgetsByType(type);
}

/**
 * Attempts to locate the widget for an element, given the ID (not widgetVar) of the widget (widget.id, not the DOM element ID widget.jq.id).
 * @deprecated Use `findWidgetById`
 * @template T Type of the widget.
 * @param id ID of an element.
 * @param widgetClass Expected type of the widget. No check is performed if not given.
 * @returns The widget for the element with the given ID, or `undefined` if no such widget exists.
 */
export function getWidgetById<T extends TConstructor>(id: string, widgetClass: T): InstanceType<T> | undefined {
  for (const widgetVar of Object.keys(PrimeFaces.widgets)) {
    if (PrimeFaces.widgets[widgetVar]?.id === id) {
      return getWidgetByVar(widgetVar, widgetClass);
    }
  }
  return undefined;
}

/**
 * Clears a p:datePicker field.
 * @param event Event that triggered this action.
 * @param source Date picker element (with the class `.p-datepicker`) or one of its children.
 */
export function clearDatePicker(event: JQuery.TriggeredEvent, source: HTMLElement): void {
  const w = findWidgetForElement(source, PrimeFaces.widget.DatePicker);
  if (w === undefined) {
    console.warn("Cannot clear date picker, no date picker widget found for source", source);
    return;
  }
  const picker = w.jq.datePicker("instance");
  if (picker === undefined) {
    console.warn("Cannot clear date picker, no date picker instance found for source", source);
    return;
  }
  picker.setDate("");
  w.cfg.onClearButtonClick?.call(picker, event);
}

/**
 * Yields control of the main thread back to the browser, returning a promise that resolves
 * to continue execution where it left off.
 *
 * This is a polyfill for `scheduler.yield()`, which is not yet supported in all browsers.
 */
export async function yieldToMain(): Promise<void> {
  if (globalThis.scheduler?.yield) {
    await globalThis.scheduler.yield();
  } else {
    await timeout(0);
  }
}

/**
 * Compresses the given string using gzip compression and encodes the result as a Base64 string.
 * @param data The string to compress and encode.
 * @returns The compressed and Base64-encoded string.
 */
export async function compressStringToBase64(data: string): Promise<string> {
  const input = new Blob([data], { type: "text/plain" });
  const reader = input.stream().pipeThrough(new CompressionStream("gzip"));
  const response = new Response(reader);
  const output = await response.blob();

  const dataUri = await readBlobAsDataUri(output);
  const index = dataUri.indexOf(",");
  return dataUri.substring(index + 1);
}
